import * as CL from '@design-system/component-library';
import { Link, useNavigate } from 'react-router';
import { paths } from '../../common/constants/pathVariables.js';
import {
  businessIdMsg,
  companiesMsg,
  companyDetailsMsg,
  devicesMsg,
  fixedBroadbandsMsg,
  fixedLineSubscriptionMsg,
  helloMsg,
  mobileBroadbandsMsg,
  mobileSubscriptionsMsg,
  omaElisaForCompaniesMsg,
  pcsMsg,
  servicesMsg,
  t,
} from '../../common/i18n/index.js';
import { HeroHeading, HeroHeadingType } from '../HeroHeading';
import { Loading } from '../Loading';

import './SelfServiceHomeHeroHeading.scss';

export interface SelfServiceHomeHeroHeadingProps {
  companyName: string;
  companyAccountsCount: number;
  businessId: string;
  serviceCounts: Record<string, number>;
  serviceLoading: Record<string, boolean>;
}

export const ServicesSection = ({
  serviceCounts,
  serviceLoading,
}: {
  serviceCounts: Record<string, number>;
  serviceLoading: Record<string, boolean>;
}) => {
  const navigate = useNavigate();

  const serviceConfig = [
    {
      key: 'mobile',
      icon: 'sim',
      title: t.OZCC(mobileSubscriptionsMsg),
      path: `${paths.PS_MOBILE_SUBSCRIPTIONS}?subscriptionType=MOBILE`,
    },
    {
      key: 'landline',
      icon: 'phone',
      title: t.N04E(fixedLineSubscriptionMsg),
      path: `${paths.PS_MOBILE_SUBSCRIPTIONS}?subscriptionType=LANDLINE`,
    },
    {
      key: 'devices',
      icon: 'devices',
      title: t.JJTM(devicesMsg),
      path: paths.PS_DEVICES,
    },
    {
      key: 'mobileBroadband',
      icon: 'mobile-broadband',
      title: t.G0EZ(mobileBroadbandsMsg),
      path: `${paths.PS_BROADBAND_SUBSCRIPTIONS}?subscriptionType=MOBILE_BROADBAND%2CMOBILE_M2M`,
    },
    {
      key: 'broadband',
      icon: 'broadband',
      title: t.YW61(fixedBroadbandsMsg),
      path: `${paths.PS_BROADBAND_SUBSCRIPTIONS}?subscriptionType=BROADBAND%2CCORPORATE_NETWORK%2CHOUSING_COMPANY_BROADBAND`,
    },
    {
      key: 'services',
      icon: 'services',
      title: t.MBG9(servicesMsg),
      path: paths.PS_SERVICES,
    },
  ];

  const noServices =
    Object.values(serviceLoading).every(loading => !loading) &&
    Object.values(serviceCounts).every(count => count === 0);
  const isLoading = Object.values(serviceLoading).some(loading => loading);

  return (
    <div className="of-self-service-home__hero-heading__services">
      <h3>{t.FO5U('Products and services in use')}</h3>
      {noServices ? (
        <div className="of-self-service-home__hero-heading__services no-services">
          <div className="of-self-service-home__hero-heading__services__magnifier-icon" />
          <h4>{t.ICJX('Your company does not have any products or services yet')}</h4>
        </div>
      ) : (
        <div className="of-self-service-home__hero-heading__services__content">
          {serviceConfig.map(service => {
            if (!serviceLoading[service.key] && serviceCounts[service.key] > 0) {
              return (
                <CL.LinkBlock
                  key={service.key}
                  href={service.path}
                  icon={<CL.Icon icon={service.icon} />}
                  onClick={() => navigate(service.path)}
                >
                  <h4>{service.title}</h4>
                  <div className="of-self-service-home__hero-heading__services__content__link-content">
                    {serviceCounts[service.key]}
                    <span className="of-self-service-home__hero-heading__services__content__link-content__unit">
                      {t.B3MG(pcsMsg)}
                    </span>
                  </div>
                </CL.LinkBlock>
              );
            }
            return null;
          })}
        </div>
      )}
      {isLoading && (
        <div className="of-self-service-home__hero-heading__services__loader-overlay">
          <Loading large bottomPadding={4} />
        </div>
      )}
    </div>
  );
};

export const SelfServiceHomeHeroHeading = ({
  companyName,
  companyAccountsCount,
  businessId,
  serviceCounts,
  serviceLoading,
}: SelfServiceHomeHeroHeadingProps) => {
  return (
    // eslint-disable-next-line react/no-unknown-property
    <div className="of-self-service-home__hero-heading" analytics-parent-element="NOE Hero section">
      <HeroHeading
        heroHeadingType={HeroHeadingType.SELFSERVICE_HOME}
        title={companyAccountsCount > 1 ? t.LOUS(omaElisaForCompaniesMsg) : companyName || t.WR9Q(helloMsg)}
        subtitle={
          <span className="of-self-service-home__hero-heading__subtitle">
            {companyAccountsCount > 1
              ? `${t.T57L(companiesMsg)}: ${t.BDBD('{}', companyAccountsCount.toString())}`
              : businessId
                ? `${t.MPA5(businessIdMsg)}: ${businessId}`
                : null}
            {(companyAccountsCount > 1 || businessId) && <span> | </span>}
            <Link to="/omaelisa/yritystiedot">{t.EHOL(companyDetailsMsg)}</Link>
          </span>
        }
        extraSection={<ServicesSection serviceCounts={serviceCounts} serviceLoading={serviceLoading} />}
      />
    </div>
  );
};
